import { createContext, useCallback, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useAuth, AuthState } from '../hooks/useAuth';
import { setGlobalAuth, setOn401Callback } from '../services/api';

interface ApiContextValue {
  auth: AuthState | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const ApiContext = createContext<ApiContextValue | undefined>(undefined);

interface ApiProviderProps {
  children: ReactNode;
}

/**
 * Provides authentication context for API calls.
 * Sets global auth synchronously before rendering children.
 * Registers 401 callback to trigger re-login on expired tokens.
 */
export function ApiProvider({ children }: ApiProviderProps) {
  const authState = useAuth();

  // Set global auth synchronously - this runs on every render but setGlobalAuth
  // is cheap (just assigns a variable)
  setGlobalAuth(authState);

  // Create stable 401 callback that triggers re-login
  const handle401 = useCallback(() => {
    if (authState.loginWithRedirect) {
      authState.loginWithRedirect();
    }
  }, [authState]);

  // Register 401 callback
  useEffect(() => {
    setOn401Callback(handle401);
    return () => setOn401Callback(null);
  }, [handle401]);

  const value = useMemo(
    () => ({
      auth: authState,
      isAuthenticated: authState.isAuthenticated,
      isLoading: authState.isLoading,
    }),
    [authState]
  );

  // Show loading state while Okta initializes
  if (authState.isEnabled && authState.isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

/**
 * Hook to access API authentication context.
 */
export function useApiAuth(): ApiContextValue {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApiAuth must be used within an ApiProvider');
  }
  return context;
}
